import { Link } from 'react-router-dom'
import { FiEye, FiSearch } from 'react-icons/fi'
import type { invoiceToTableRow } from '@/services/api/finance'

export type InvoiceTableRow = ReturnType<typeof invoiceToTableRow>

export function getInvoiceStatusStyle(status: string) {
  switch (status) {
    case 'Paid':
      return { textColor: '#13AB5B', bgColor: '#E8F7EF' }
    case 'Pending':
      return { textColor: '#EEC233', bgColor: '#FEF9EB' }
    case 'Overdue':
      return { textColor: '#E93323', bgColor: '#FEE2E2' }
    default:
      return { textColor: '#69697A', bgColor: '#F3F4F6' }
  }
}

const thStyle: React.CSSProperties = {
  fontFamily: 'Lexend Deca',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '20px',
  color: '#69697A',
  paddingLeft: '16px',
  paddingTop: '10px',
  paddingBottom: '10px',
}

const tdStyle: React.CSSProperties = {
  fontFamily: 'Lexend Deca',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '20px',
  color: '#212126',
  paddingLeft: '16px',
}

const GREEN_DOWNLOAD_FILTER =
  'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(103deg) brightness(95%) contrast(85%)'

interface FinanceInvoicesTableCardProps {
  invoices: InvoiceTableRow[]
  loading?: boolean
  search: string
  onSearchChange: (value: string) => void
  emptyMessage?: string
  viewAllHref?: string
  onExport?: () => void
  onViewInvoice?: (invoice: InvoiceTableRow) => void
  onDownloadInvoice?: (invoice: InvoiceTableRow) => void
}

export default function FinanceInvoicesTableCard({
  invoices,
  loading = false,
  search,
  onSearchChange,
  emptyMessage = 'No invoices yet. Sync from Xero (Settings → Integrations) or add an invoice.',
  viewAllHref,
  onExport,
  onViewInvoice,
  onDownloadInvoice,
}: FinanceInvoicesTableCardProps) {
  const query = search.trim().toLowerCase()
  const filtered = query
    ? invoices.filter(
        (invoice) =>
          (invoice.number && invoice.number.toLowerCase().includes(query)) ||
          (invoice.ref && invoice.ref.toLowerCase().includes(query)) ||
          (invoice.status && invoice.status.toLowerCase().includes(query))
      )
    : invoices

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <style>{`
        .finance-invoices-search::placeholder {
          font-family: 'Lexend Deca';
          font-weight: 400;
          font-size: 14px;
          line-height: 22px;
          color: #69697A;
        }
      `}</style>

      <div className="flex flex-col gap-3 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <h3
            className="flex-shrink-0"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '28px',
              color: '#212126',
            }}
          >
            Invoices
          </h3>

          <div className="flex flex-1 flex-col sm:flex-row sm:items-center gap-2 min-w-0">
            <div className="relative flex-1 min-w-0">
              <FiSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ width: '18px', height: '18px', color: '#69697A' }}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search Invoices"
                className="finance-invoices-search w-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{
                  height: '38px',
                  borderRadius: '8px',
                  border: '1px solid #E9E9EA',
                  padding: '8px 12px 8px 40px',
                  background: '#FFFFFF',
                  fontFamily: 'Lexend Deca',
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126',
                }}
              />
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {viewAllHref && (
                <Link
                  to={viewAllHref}
                  className="flex items-center justify-center whitespace-nowrap hover:opacity-90 transition-opacity"
                  style={{
                    height: '38px',
                    minWidth: '78px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #2C71F6',
                    backgroundColor: '#FFFFFF',
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#2C71F6',
                    textDecoration: 'none',
                  }}
                >
                  View all
                </Link>
              )}
              {onExport && (
                <button
                  type="button"
                  onClick={onExport}
                  className="flex items-center justify-center whitespace-nowrap hover:opacity-90 transition-opacity"
                  style={{
                    height: '38px',
                    minWidth: '78px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #212126',
                    backgroundColor: '#212126',
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#FFFFFF',
                  }}
                >
                  Export
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="overflow-x-hidden"
        style={{ borderRadius: '8px', border: '1px solid #E9E9EA', minWidth: '100%' }}
      >
        <table className="w-full" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #E9E9EA' }}>
              <th className="text-left" style={{ ...thStyle, borderTopLeftRadius: '8px', width: '24%' }}>Number</th>
              <th className="text-left" style={{ ...thStyle, width: '40%' }}>Ref</th>
              <th className="text-left" style={{ ...thStyle, width: '20%' }}>Status</th>
              <th
                className="text-left"
                style={{
                  ...thStyle,
                  borderTopRightRadius: '8px',
                  width: '16%',
                  paddingRight: '16px',
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="py-8 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              filtered.map((invoice, index) => {
                const statusStyle = getInvoiceStatusStyle(invoice.status)
                const isLastRow = index === filtered.length - 1
                return (
                  <tr key={invoice.id ?? index} style={{ borderBottom: isLastRow ? 'none' : '1px solid #E9E9EA' }}>
                    <td
                      className="py-3.5"
                      style={{
                        ...tdStyle,
                        ...(isLastRow && { borderBottomLeftRadius: '8px' }),
                      }}
                    >
                      {invoice.number}
                    </td>
                    <td className="py-3.5" style={tdStyle}>
                      <span className="block truncate pr-2" title={invoice.ref}>
                        {invoice.ref}
                      </span>
                    </td>
                    <td className="py-3.5" style={tdStyle}>
                      <span
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '20px',
                          color: statusStyle.textColor,
                          backgroundColor: statusStyle.bgColor,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          display: 'inline-block',
                        }}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td
                      className="py-3.5"
                      style={{
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        ...(isLastRow && { borderBottomRightRadius: '8px' }),
                      }}
                    >
                      <div className="flex items-center gap-1.5 w-fit max-w-full">
                        <button
                          type="button"
                          aria-label={`View invoice ${invoice.number}`}
                          onClick={() => onViewInvoice?.(invoice)}
                          className="rounded flex items-center justify-center hover:opacity-80 transition-opacity"
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            backgroundColor: '#EAF1FF',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          <FiEye style={{ width: '16px', height: '16px', color: '#2C71F6' }} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Download invoice ${invoice.number}`}
                          onClick={() => onDownloadInvoice?.(invoice)}
                          className="rounded flex items-center justify-center hover:opacity-80 transition-opacity"
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            backgroundColor: '#E8F7EF',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          <img
                            src="/images/download.svg"
                            alt=""
                            style={{ width: '16px', height: '16px', filter: GREEN_DOWNLOAD_FILTER }}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
